"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTenancy } from "@/contexts/TenancyContext";
import type { User as TenancyUser } from "@/contexts/TenancyContext";

interface UserOption {
  value: string;
  label: string;
}

interface SingleUserComboboxProps {
  selected: string;
  setSelected: (value: string) => void;
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  readOnly?: boolean;
  minimal?: boolean;
  hideChevron?: boolean;
}

export const SingleUserCombobox: React.FC<SingleUserComboboxProps> = ({ selected, setSelected, placeholder, className, buttonClassName, variant = "outline", readOnly = false, minimal = false, hideChevron = false }) => {
  const { users, userIdToName } = useTenancy();

  const options = React.useMemo<TenancyUser[]>(() => {
    const base = [...users].sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    if (!selected || base.some(u => u.id === selected)) {
      return base;
    }

    const displayName = userIdToName?.[selected] || selected;
    const parts = displayName.split(/\s+/).filter(Boolean);
    const firstName = parts.shift() || displayName;
    const lastName = parts.join(' ');
    const fallbackUser: TenancyUser = {
      id: selected,
      firstName,
      lastName,
      email: '',
      roles: {},
    };

    return [...base, fallbackUser];
  }, [users, selected, userIdToName]);

  const formatUserLabel = React.useCallback((user: TenancyUser) => {
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    if (user.email) {
      return `${name} <${user.email}>`.trim();
    }
    if (userIdToName?.[user.id]) {
      return userIdToName[user.id];
    }
    return name || user.id;
  }, [userIdToName]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);

  const selectedUser = options.find(u => u.id === selected);

  if (readOnly) {
    return (
      <div className={cn("w-full px-3 py-2 rounded border bg-muted text-muted-foreground", className)}>
        {selectedUser
          ? formatUserLabel(selectedUser)
          : <span className="text-muted-foreground">{placeholder || "No user selected"}</span>
        }
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {minimal ? (
          <button
            role="combobox"
            aria-expanded={open}
            className={cn(
              "flex items-center justify-start bg-transparent border-0 p-0 h-auto w-auto focus:outline-none cursor-pointer hover:bg-transparent",
              className,
              buttonClassName
            )}
            type="button"
            disabled={loading}
          >
            <span className="truncate flex-1 text-left whitespace-nowrap text-ellipsis max-w-full overflow-hidden" title={selectedUser ? formatUserLabel(selectedUser) : (placeholder || 'Select user...')}>
              {loading
                ? 'Loading users...'
                : selectedUser
                  ? formatUserLabel(selectedUser)
                  : (placeholder || "Select user...")}
            </span>
            {!hideChevron && <ChevronsUpDown className="ml-1 h-3 w-3 flex-shrink-0 opacity-50" />}
          </button>
        ) : (
          <Button
            variant={variant}
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full min-w-0 max-w-full flex-nowrap justify-between overflow-hidden text-ellipsis",
              className,
              buttonClassName
            )}
            type="button"
            disabled={loading}
          >
            <span className="truncate flex-1 text-left whitespace-nowrap text-ellipsis max-w-full overflow-hidden" title={selectedUser ? formatUserLabel(selectedUser) : (placeholder || 'Select user...')}>
              {loading
                ? 'Loading users...'
                : selectedUser
                  ? formatUserLabel(selectedUser)
                  : (placeholder || "Select user...")}
            </span>
            {!hideChevron && <ChevronsUpDown className="ml-2 h-4 w-4 flex-shrink-0 opacity-50" />}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="min-w-[250px] w-[--radix-popover-trigger-width] max-w-[400px] p-0 z-[9999]"
        sideOffset={4}
        collisionPadding={8}
        avoidCollisions={true}
      >
        <Command
          filter={(value, search) => {
            // Always allow exact match
            if (value.toLowerCase() === search.toLowerCase()) return 1;
            const trimmedValue = value.trim();
            const user = options.find(u => formatUserLabel(u) === trimmedValue);
            if (!user) return 0;
            const searchLower = search.toLowerCase();
            if (!searchLower) return 1;
            // Match on name or email substring
            const nameMatch = `${user.firstName} ${user.lastName}`.toLowerCase();
            const emailMatch = (user.email ?? '').toLowerCase();
            const labelMatch = formatUserLabel(user).toLowerCase();
            if (nameMatch.includes(searchLower) || emailMatch.includes(searchLower) || labelMatch.includes(searchLower)) {
              return 1;
            }
            return 0;
          }}
        >
          <CommandInput placeholder="Search users..." disabled={loading} />
          <CommandList className="max-h-[400px] overflow-auto" onWheel={e => e.stopPropagation()}>
            {error && <CommandEmpty>{error}</CommandEmpty>}
            {!error && loading && <CommandEmpty>Loading users...</CommandEmpty>}
            {!error && !loading && options.length === 0 && <CommandEmpty>No users found.</CommandEmpty>}
            <CommandGroup>
              {options.map((user) => (
                <CommandItem
                  key={user.id}
                  value={formatUserLabel(user)}
                  onSelect={() => {
                    setSelected(user.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected === user.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {formatUserLabel(user)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
